import { useCallback, useMemo, useState } from 'react';
import { isPhoneNative } from '@helpers';
import { getBoxShadowStyle } from '@styles/shadow';
import { BOX_SHADOW_TYPE } from '@components/ncFileVersions/constants';
import { NCFileVersion as NCFileVersionType } from '@interfaces/search';
import type { LeftColumnProps } from '@components/ncFileVersions/types';
import { sortVersionsBySequence } from '@components/ncFileVersions/helpers/sortVersionsBySequence';
import { buildFavoriteCaption } from '@components/ncFileVersions/helpers/buildFavoriteCaption';
import { buildInactiveToggleText } from '@components/ncFileVersions/helpers/buildInactiveToggleText';

export interface UseManageLeftColumnParams
  extends Pick<
    LeftColumnProps,
    | 'C'
    | 'favorite'
    | 'otherVersions'
    | 'comparisonMode'
    | 'versionsToCompare'
    | 'selectedVersion'
    | 'onSelectVersion'
    | 'onToggleVersionForComparison'
    | 'machineWhichRanFavoriteOrLatest'
    | 'shouldDisplayFilterInactiveVersionsButton'
    | 'showOnlyActiveVersions'
    | 'inactiveVersionsCount'
  > {}

export const useManageLeftColumn = ({
  C,
  favorite,
  otherVersions,
  comparisonMode = false,
  versionsToCompare = [],
  selectedVersion,
  onSelectVersion,
  onToggleVersionForComparison,
  machineWhichRanFavoriteOrLatest,
  shouldDisplayFilterInactiveVersionsButton,
  showOnlyActiveVersions,
  inactiveVersionsCount = 0,
}: UseManageLeftColumnParams) => {
  const shadowStyles = getBoxShadowStyle({ type: BOX_SHADOW_TYPE, C });
  const [sortAscending, setSortAscending] = useState(false);

  const toggleSort = useCallback(() => setSortAscending((prev) => !prev), []);

  const sortedOtherVersions = useMemo(() => sortVersionsBySequence(otherVersions, sortAscending), [otherVersions, sortAscending]);

  const handleVersionClick = useCallback(
    (version: NCFileVersionType) => {
      if (comparisonMode && onToggleVersionForComparison) {
        onToggleVersionForComparison(version);
        return;
      }
      onSelectVersion?.(version);
    },
    [comparisonMode, onToggleVersionForComparison, onSelectVersion]
  );

  // A row shows selected by its compare-selection in comparison mode, otherwise by the viewed version — never on phone, where selecting a version navigates away instead.
  const isSelected = useCallback(
    (version: NCFileVersionType) => {
      if (comparisonMode) {
        return versionsToCompare.some((v) => v.ncFileVersionId === version.ncFileVersionId);
      }
      if (isPhoneNative) {
        return false;
      }
      return version?.ncFileVersionId === selectedVersion?.ncFileVersionId;
    },
    [comparisonMode, versionsToCompare, selectedVersion]
  );

  const favoriteCaption = buildFavoriteCaption({ machine: machineWhichRanFavoriteOrLatest, isFavorite: Boolean(favorite) });

  const hasOtherVersions = sortedOtherVersions.length > 0;
  const showSortButton = sortedOtherVersions.length > 1;
  const showInactiveToggle = Boolean(shouldDisplayFilterInactiveVersionsButton) && inactiveVersionsCount > 0;
  const inactiveToggleText = buildInactiveToggleText({ showOnlyActiveVersions, inactiveVersionsCount });

  return {
    shadowStyles,
    toggleSort,
    sortedOtherVersions,
    handleVersionClick,
    isSelected,
    favoriteCaption,
    hasOtherVersions,
    showSortButton,
    showInactiveToggle,
    inactiveToggleText,
  };
};
